// src/components/Header.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './Header.css';
import DigitalFlyerPopup from './DigitalFlyerPopup';
import { useTheme } from '../contexts/ThemeContext';
import { getProfile } from '../services/profileService';
import { getCurrentPositionAsync, reverseGeocode } from '../services/locationService';

function Header({
  isAuthenticated,
  setIsAuthenticated,
  selectedDate,
  setSelectedDate,
  selectedYear,
  setSelectedYear,
  selectedMonth,
  setSelectedMonth,
  selectedWeek,
  setSelectedWeek,
  showCalendar,
  setShowCalendar,
  userLocation,
  setUserLocation,
  onAskTudu
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [showFlyer, setShowFlyer] = useState(false);
  // Remove local userLocation state
  const [manualLocation, setManualLocation] = useState('');
  const [locating, setLocating] = useState(false);
  const [profile, setProfile] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Current account identity, read fresh from storage on every render so the
  // greeting/avatar update immediately when you sign in as a different user
  // (no stale name carried over from the previous account). The access token is
  // the reliable identity key — it's always set on login (even Google sign-in,
  // which may not store an email) and always changes between accounts.
  const userEmail = localStorage.getItem('userEmail') || '';
  const accessToken = localStorage.getItem('accessToken') || '';
  const userName = userEmail
    ? userEmail.split('@')[0].replace(/^\w/, (c) => c.toUpperCase())
    : '';

  // Real profile (display name / handle / avatar) so the header + dropdown match
  // the Profile page instead of a name guessed from the email. Cleared first and
  // re-fetched whenever the signed-in account (token) or auth state changes.
  useEffect(() => {
    let active = true;
    setProfile(null);
    if (isAuthenticated) {
      getProfile().then((p) => { if (active) setProfile(p); }).catch(() => {});
    }
    return () => { active = false; };
  }, [isAuthenticated, accessToken]);

  // Display name shown in the greeting + dropdown. Prefer the profile's
  // display_name, then username, then the email-derived fallback.
  const displayName = profile?.display_name || profile?.username || userName;
  const dropdownRef = useRef(null);
  const calendarRef = useRef(null);
  const locationRef = useRef(null);

  const persistLocation = (newLoc) => {
    setUserLocation(newLoc);
    localStorage.setItem('userLocation', JSON.stringify(newLoc));
  };

  const handleLocationSelect = async (loc) => {
    setIsLocationOpen(false);

    if (loc === 'Current Location' || loc === 'Use Current Location') {
      // Actually detect the device location, then reverse-geocode it to a city
      // label. Coordinates are stored too so task store-search + Maps links can
      // anchor to them.
      setLocating(true);
      try {
        const { lat, lng } = await getCurrentPositionAsync();
        let resolved = { label: 'Current Location', city: '', region: '', country: '' };
        try { resolved = await reverseGeocode(lat, lng); } catch (_) { /* keep coords, generic label */ }
        persistLocation({
          type: 'current',
          name: resolved.label || 'Current Location',
          lat,
          lng,
          city: resolved.city,
          region: resolved.region,
          country: resolved.country,
        });
      } catch (err) {
        // Permission denied / unavailable — fall back to the plain label.
        persistLocation({ type: 'current', name: 'Current Location' });
        if (err && err.code === 1) {
          window.alert('Location permission was blocked. Enable it in your browser to use your current location.');
        }
      } finally {
        setLocating(false);
      }
    } else {
      persistLocation({ type: 'custom', name: loc });
    }
  };

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Close dropdown and calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
      if (locationRef.current && !locationRef.current.contains(event.target)) {
        setIsLocationOpen(false);
      }
    };

    if (isProfileOpen || showCalendar || isLocationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileOpen, showCalendar, isLocationOpen, setShowCalendar]);

  const handleLogoClick = () => {
    navigate('/dashboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userEmail');
    setIsAuthenticated(false);
    navigate('/login');
  };

  const toggleProfileDropdown = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  const toggleCalendar = () => {
    setShowCalendar(!showCalendar);
  };

  const handleCalendarDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedYear(date.getFullYear());
    setSelectedMonth(date.getMonth() + 1);
    setSelectedWeek(Math.ceil(date.getDate() / 7));
    setShowCalendar(false);
    // Navigate to dashboard if not already there
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
  };

  const getGreeting = () => {
    const h = currentTime.getHours();
    if (h < 12) return 'Good Morning';
    if (h < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const formatDateTime = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const day = days[currentTime.getDay()];
    const month = months[currentTime.getMonth()];
    const date = currentTime.getDate();

    return `${day}, ${month} ${date}`;
  };

  const handleMenuItemClick = (action) => {
    setIsProfileOpen(false);

    switch (action) {
      case 'profile':
        navigate('/profile');
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'activity':
        navigate('/activity');
        break;
      case 'notifications':
        // TODO: Implement notifications feature
        console.log('Notifications clicked');
        break;
      case 'help':
        navigate('/help');
        break;
      case 'darkMode':
        toggleTheme();
        break;
      case 'logout':
        handleLogout();
        break;
      default:
        break;
    }
  };

  const isLandingPage = location.pathname === '/';

  // Live local time + timezone abbreviation, revealed on hover of the location
  // pill. Derived from currentTime (already ticking every minute), so HH:MM
  // stays accurate without an extra interval.
  const locTime = currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  const tzAbbr = (() => {
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(currentTime);
      return parts.find((p) => p.type === 'timeZoneName')?.value || '';
    } catch (_) { return ''; }
  })();

  return (
    <header className={`glass-header ${!isLandingPage ? 'dashboard-header' : ''}`}>
      {/* Left Side: Logo */}
      <div className="header-group-left">
        <div className="header-left" onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
          <span className="tudu-brand">Tudu</span>
        </div>

        {/* Location Selector */}
        <div className="location-selector" ref={locationRef}>
          <button className="location-btn" onClick={() => setIsLocationOpen(!isLocationOpen)}>
            {/* Live "active location" pulse */}
            <span className="loc-pulse" aria-hidden="true">
              <span className="loc-pulse-ping" />
              <span className="loc-pulse-dot" />
            </span>

            <svg className="location-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
              <circle cx="12" cy="10" r="3"></circle>
            </svg>

            {/* Location name; slides up to reveal the live local time on hover */}
            <span className="location-reveal">
              <span className="location-reveal-inner">
                <span className="location-text">{locating ? 'Locating…' : (userLocation?.name || userLocation)}</span>
                <span className="location-time">{locTime}{tzAbbr ? ` ${tzAbbr}` : ''}</span>
              </span>
            </span>

            <svg className={`chevron-icon ${isLocationOpen ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>

          {isLocationOpen && (
            <div className="location-dropdown">
              <div className="location-option current" onClick={() => handleLocationSelect('Current Location')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <span>Use Current Location</span>
              </div>

              <div className="location-manual-box">
                <input
                  type="text"
                  placeholder="Enter location..."
                  value={manualLocation}
                  onChange={(e) => setManualLocation(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && manualLocation.trim()) {
                      handleLocationSelect(manualLocation);
                      setManualLocation('');
                    }
                  }}
                  className="location-input"
                />
              </div>

              <div className="location-divider"></div>
              <div className="location-option" onClick={() => handleLocationSelect('New York, USA')}>New York, USA</div>
              <div className="location-option" onClick={() => handleLocationSelect('London, UK')}>London, UK</div>
              <div className="location-option" onClick={() => handleLocationSelect('Tokyo, Japan')}>Tokyo, Japan</div>
              <div className="location-option" onClick={() => handleLocationSelect('San Francisco, CA')}>San Francisco, CA</div>
            </div>
          )}
        </div>

        {/* Greeting — sits beside the logo/location on the header for logged-in
            users; the marketing nav links stay in the center. */}
        {isAuthenticated && (
          <div
            className="header-greeting"
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '5px',
              marginLeft: '8px',
              fontFamily: 'var(--tudu-font-display)',
              fontWeight: 700,
              fontSize: '1.05rem',
              color: 'var(--tudu-text-hi)',
              whiteSpace: 'nowrap',
            }}
          >
            <span>{getGreeting()},</span>
            <span style={{ color: 'var(--tudu-brand)' }}>{displayName || 'there'}!</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="nav-links">
        <Link to="/">Home</Link>
        <Link to="/about">About Us</Link>
        <Link to="/contact">Contact</Link>
      </nav>

      {/* Right Side Group */}
      <div className="header-group-right">
        {/* Ask Tudu assistant — opens the AI chat window (rendered at shell level) */}
        {isAuthenticated && (
          <button className="datetime-btn ask-tudu-btn" onClick={onAskTudu} aria-label="Ask Tudu assistant">
            <svg className="ask-tudu-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9z"></path>
              <path d="M19 14l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z"></path>
            </svg>
            <span className="datetime-text">Ask</span>
          </button>
        )}

        {/* Flyer Notification */}
        {isAuthenticated && (
          <div className="flyer-notification" style={{ position: 'relative' }}>
            <button className="datetime-btn" onClick={() => setShowFlyer(true)} aria-label="View Deals">
              <span className="notification-dot" style={{ position: 'absolute', top: '-2px', right: '-2px', width: '10px', height: '10px', background: '#ff4d4f', borderRadius: '50%', border: '2px solid white' }}></span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="calendar-icon">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                <line x1="7" y1="7" x2="7.01" y2="7"></line>
              </svg>
              <span className="datetime-text">Deals</span>
            </button>
          </div>
        )}

        {/* Calendar & Time Display */}
        {isAuthenticated && (
          <div className="calendar-time-section" ref={calendarRef}>
            <button className="datetime-btn" onClick={toggleCalendar} aria-label="Open calendar">
              <svg className="calendar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span className="datetime-text">{formatDateTime()}</span>
            </button>

            {showCalendar && (
              <div className="header-calendar-popup">
                <Calendar
                  onChange={handleCalendarDateSelect}
                  value={selectedDate}
                  calendarType="iso8601"
                />
              </div>
            )}
          </div>
        )}

        {/* Auth buttons */}
        <div className="auth-buttons">
          {isAuthenticated ? (
            <div className="profile-dropdown" ref={dropdownRef}>
              <button
                className="btn profile-btn"
                onClick={toggleProfileDropdown}
                aria-label="Profile menu"
              >
                <svg
                  className="profile-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
                </svg>
                <span className="profile-name">Profile</span>
                <svg
                  className={`chevron-icon ${isProfileOpen ? 'open' : ''}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {isProfileOpen && (
                <div className="dropdown-menu">
                  <div className="dropdown-header">
                    <div className="user-avatar">
                      {profile?.avatar_image
                        ? <img className="user-avatar-img" src={profile.avatar_image} alt="avatar" />
                        : (displayName ? displayName.charAt(0).toUpperCase() : 'U')}
                    </div>
                    <div className="user-info">
                      <div className="user-name">{displayName || 'User'}</div>
                      <div className="user-email">
                        {profile?.username ? `@${profile.username}` : (localStorage.getItem('userEmail') || 'user@tudu.ai')}
                      </div>
                    </div>
                  </div>

                  <div className="dropdown-divider"></div>

                  <div className="dropdown-items">
                    <button className="dropdown-item" onClick={() => handleMenuItemClick('profile')}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
                      </svg>
                      <span>My Profile</span>
                    </button>

                    <button className="dropdown-item" onClick={() => handleMenuItemClick('settings')}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M12 1v6m0 6v6M1 12h6m6 0h6m-2.5-6.5l-4 4m0 5l4 4m-9.5 0l4-4m0-5l-4-4" />
                      </svg>
                      <span>Settings</span>
                    </button>

                    <button className="dropdown-item" onClick={() => handleMenuItemClick('activity')}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                      </svg>
                      <span>Activity</span>
                    </button>

                    <button className="dropdown-item" onClick={() => handleMenuItemClick('notifications')}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                      </svg>
                      <span>Notifications</span>
                    </button>

                    <button className="dropdown-item" onClick={() => handleMenuItemClick('help')}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                      <span>Help & Support</span>
                    </button>

                    <div className="dropdown-divider"></div>

                    <div className="dropdown-item dark-mode-toggle">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                      </svg>
                      <span>Dark Mode</span>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={isDark}
                          onChange={toggleTheme}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>

                    <div className="dropdown-divider"></div>

                    <button className="dropdown-item logout-item" onClick={() => handleMenuItemClick('logout')}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : !isLandingPage ? (
            <>
              <Link to="/login">
                <button className="btn btn-outline">Login</button>
              </Link>
              <Link to="/signup">
                <button className="btn btn-solid">Sign Up</button>
              </Link>
            </>
          ) : null}
        </div>
      </div>

      {showFlyer && <DigitalFlyerPopup onClose={() => setShowFlyer(false)} />}
    </header>
  );
}

export default Header;
